import { useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import Cookies from "js-cookie";
import { persistor } from "../lib/redux/store";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearUser } from "../lib/redux/actions";

const INACTIVITY_LIMIT_MINUTES = 10; // set to 10 min

export default function useSilentTokenRefresh() {
  const { instance } = useMsal();
  const dispatch = useDispatch();
  const router = useNavigate();

  useEffect(() => {
    const request = { scopes: ["User.Read"] };

    const refreshToken = async () => {
      try {
        const acct = instance.getAllAccounts()[0];
        if (!acct) return;

        const res = await instance.acquireTokenSilent({
          ...request,
          account: acct,
          forceRefresh: true,
        });

        Cookies.set("idToken", res.idToken, {
          secure: true,
          sameSite: "Strict",
          expires: 1,
          path: "/",
        });

        if (res?.idTokenClaims?.exp) {
          const expiryDate = new Date(res.idTokenClaims.exp * 1000);
          Cookies.set("tokenExpiry", expiryDate.toISOString(), { path: "/" });

          const msUntilRefresh =
            expiryDate.getTime() - Date.now() - (5 * 60 * 1000);
          if (msUntilRefresh > 0) {
            setTimeout(refreshToken, msUntilRefresh);
          }
        }
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          console.warn("⚠ Token refresh requires interaction");
        } else {
          console.error("❌ Token refresh error:", err);
        }
      }
    };

    const logoutUser = () => {
      Cookies.remove("idToken", { path: "/" });
      Cookies.remove("tokenExpiry", { path: "/" });
      Cookies.remove("lastActiveTime", { path: "/" });
      sessionStorage.clear();
      persistor.purge();
      router("/auth");
      setTimeout(() => {
        dispatch(clearUser());
      }, 1000);
    };

    const restoreSession = async () => {
      // 🔹 Check inactivity on fresh page load (tab close + reopen)
      const lastActive = Cookies.get("lastActiveTime");
      if (lastActive) {
        const inactiveMinutes =
          (Date.now() - parseInt(lastActive, 10)) / (1000 * 60);
        if (inactiveMinutes >= INACTIVITY_LIMIT_MINUTES) {
          logoutUser();
          return;
        }
      }

      const tokenExpiry = Cookies.get("tokenExpiry");
      const idToken = Cookies.get("idToken");
      if (!idToken) return;

      if (tokenExpiry) {
        const expiryTime = new Date(tokenExpiry).getTime();
        if (Date.now() >= expiryTime) {
          console.warn("Token already expired — refreshing now");
          await refreshToken();
          return;
        }
      }

      const accounts = instance.getAllAccounts();
      if (!accounts.length) {
        try {
          const ssoRes = await instance.ssoSilent(request);
          Cookies.set("idToken", ssoRes.idToken, {
            secure: true,
            sameSite: "Strict",
            expires: 1,
            path: "/",
          });
        } catch (err) {
          console.warn("⚠ SSO Silent failed:", err);
          return;
        }
      }

      refreshToken();
    };

    const handleTabLeave = () => {
      Cookies.set("lastActiveTime", Date.now().toString(), { path: "/" });
    };

    const handleTabFocus = () => {
      const lastActive = Cookies.get("lastActiveTime");
      if (lastActive) {
        const inactiveMinutes =
          (Date.now() - parseInt(lastActive, 10)) / (1000 * 60);
        if (inactiveMinutes >= INACTIVITY_LIMIT_MINUTES) {
          logoutUser();
        } else {
          refreshToken();
        }
      } else {
        refreshToken();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleTabFocus();
      }
    };

    // Track leaving and returning
    window.addEventListener("beforeunload", handleTabLeave);
    window.addEventListener("blur", handleTabLeave);
    window.addEventListener("focus", handleTabFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Check on initial mount (fresh load)
    restoreSession();

    return () => {
      window.removeEventListener("beforeunload", handleTabLeave);
      window.removeEventListener("blur", handleTabLeave);
      window.removeEventListener("focus", handleTabFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance]);
}
